import VirtualBackgroundExtension, { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background';
import AgoraRTC, { type ICameraVideoTrack, ILocalVideoTrack } from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger';
import { AGORA_ENCODER_CONFIG } from '@studio/constants';

/* eslint-disable no-unused-vars */
export enum BACKGROUND_OPTIONS {
  BLUR = 'Blur',
  CUSTOM = 'Custom',
  NONE = 'None'
}

const LOG = new StudioLogger('agoraVirtualBackgroundLogger');

const extension = new VirtualBackgroundExtension();

let processor: IVirtualBackgroundProcessor | null = null;
let previewProcessor: IVirtualBackgroundProcessor | null = null;
let previewVideoTrack: ICameraVideoTrack | null = null;

/**
 * Configuration for blur virtual background effect
 */
interface BlurVFXConfig {
  type: 'blur';
  blurDegree: number;
}

/**
 * Configuration for custom image virtual background effect
 */
interface ImageVFXConfig {
  type: 'img';
  source: HTMLImageElement;
}

/**
 * Configuration for color virtual background effect
 */
interface ColorVFXConfig {
  type: 'color';
  color: string;
}

/**
 * Type for the transformed video input
 */
interface TransformedVideoInput {
  transformedVideoTrack: ICameraVideoTrack;
  device: string;
}

/**
 * Type for all possible VFX configurations
 */
type VFXConfig = BlurVFXConfig | ImageVFXConfig | ColorVFXConfig;

type OperatingSystem = 'Windows' | 'MacOS' | 'Linux' | 'Android' | 'iOS' | 'Unknown';

/**
 * Helper to detect operating system from user agent
 */
export const detectOS = (): OperatingSystem => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('win') !== -1) return 'Windows';
  if (userAgent.indexOf('mac') !== -1) return 'MacOS';
  if (userAgent.indexOf('linux') !== -1) return 'Linux';
  if (userAgent.indexOf('android') !== -1) return 'Android';
  if (userAgent.indexOf('ios') !== -1 || (userAgent.indexOf('mac') !== -1 && navigator.maxTouchPoints > 0))
    return 'iOS';
  return 'Unknown';
};

/**
 * Get appropriate WASM file path based on browser and OS
 */
export const getWasmPath = (): string => {
  // Use the latest version of the WASM file from CDN that matches our dependency
  const baseUrl: string = 'https://cdn.jsdelivr.net/npm/agora-extension-virtual-background@2.1.0/wasms';

  // For Safari and other browsers
  const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const os: OperatingSystem = detectOS();

  console.log('Detected OS and browser', { os, isSafari });

  if (isSafari) {
    return `${baseUrl}/agora-wasm-safari.wasm`;
  }

  return `${baseUrl}/agora-wasm.wasm`;
};

const wasmPath: string = getWasmPath();

/**
 * Checks device compatibility for the virtual background feature
 * @returns {boolean}
 * @throws {Error} When virtual background is not supported

 */
const checkDeviceCompatibility = (): boolean => {
  const isSupported = extension.checkCompatibility();
  if (!isSupported) {
    throw new Error(`Virtual background is not supported`);
  }

  return isSupported;
};

/**
 * Initializes the virtual background extension
 * @returns {void}
 */
export const initializeVirtualBackground = async (): Promise<void> => {
  if (processor) return;
  try {
    checkDeviceCompatibility();
    AgoraRTC.registerExtensions([extension]);
    processor = extension.createProcessor();
    await processor.init(wasmPath);
    LOG.info('Virtual background processor initialized successfully');
  } catch (e) {
    processor = null;
    const error = e as Error;
    throw new Error(`Error initializing virtual background: ${error.message}`);
  }
};

/**
 * Helper function to safely cleanup processor pipeline
 * @param processor - The processor to cleanup
 * @param videoTrack - Optional video track to unpipe
 * @returns {Promise<void>}
 */
export const resetProcessorPipeline = async (
  processor: IVirtualBackgroundProcessor | null,
  videoTrack?: ICameraVideoTrack
): Promise<void> => {
  if (!processor) return;

  try {
    await processor.disable();
    if (videoTrack) {
      videoTrack.unpipe();
    }
    processor.unpipe();
  } catch (e) {
    const error = e as Error;
    LOG.error('Failed to cleanup processor pipeline', error.message);
  }
};

/**
 * Creates a new transformed video device with background filter applied
 * @param videoTrack
 * @param videoFxConfig
 * @param videoInputId
 * @param isPreview - If true, uses preview processor, otherwise uses main processor
 * @returns {Promise<TransformedVideoInput>}
 */
export const transformVideo = async (
  videoTrack: ICameraVideoTrack,
  videoFxConfig: VFXConfig,
  videoInputId: string,
  isPreview = false
): Promise<{ transformedVideoTrack: ICameraVideoTrack; device: string }> => {
  if (isPreview) {
    await initializePreviewProcessor();
  } else {
    await initializeVirtualBackground();
  }

  const currentProcessor = isPreview ? previewProcessor! : processor!;
  const processorName = isPreview ? 'Preview' : 'Main';

  try {
    // Disable processor before making changes
    await resetProcessorPipeline(currentProcessor, videoTrack);

    // Set new options and reconnect the pipeline
    currentProcessor.setOptions(videoFxConfig);
    videoTrack.pipe(currentProcessor).pipe(videoTrack.processorDestination);
    await currentProcessor.enable();
    LOG.info(`${processorName} video track transformed with virtual background`, { config: videoFxConfig.type });

    return {
      transformedVideoTrack: videoTrack,
      device: videoInputId
    };
  } catch (e) {
    const error = e as Error;
    LOG.error(`Error transforming ${processorName} video track`, error.message);
    throw new Error(`Failed to transform ${processorName} video track: ${error.message}`);
  }
};

/**
 * Updates the background filter configuration without recreating the video track
 * @param videoFxConfig - The new VFX configuration to apply
 * @param videoTrack - Video track to repipe with new processor settings
 * @param isPreview - If true, updates preview processor, otherwise updates main processor
 * @returns {Promise<void>}
 */
export const updateBackgroundFilter = async (
  videoFxConfig: VFXConfig,
  videoTrack: ICameraVideoTrack,
  isPreview = false
): Promise<void> => {
  const currentProcessor = isPreview ? previewProcessor : processor;
  const processorName = isPreview ? 'preview' : 'main';

  if (!currentProcessor) {
    LOG.error(`No ${processorName} processor found`);
    return;
  }

  try {
    // Cleanup: disable and unpipe
    await resetProcessorPipeline(currentProcessor, videoTrack);

    currentProcessor.setOptions(videoFxConfig);

    videoTrack.pipe(currentProcessor).pipe(videoTrack.processorDestination);

    // Re-enable processor with new configuration
    await currentProcessor.enable();
    LOG.info(`Updated ${processorName} background filter configuration`, { videoFxConfig });
  } catch (e) {
    const error = e as Error;
    LOG.error(`Error updating ${processorName} background filter`, error.message);
    throw new Error(`Failed to update ${processorName} background filter: ${error.message}`);
  }
};

/**
 * Disables and releases virtual background processor completely
 * @param transformedVideoInput - Optional transformed video input to unpipe from processor
 * @returns {void}
 */
export const clearVirtualBackground = async (transformedVideoInput?: TransformedVideoInput | null): Promise<void> => {
  if (!processor) {
    LOG.error('No processor found');
    return;
  }

  // Cleanup: disable and unpipe
  try {
    await processor.disable();
    if (transformedVideoInput?.transformedVideoTrack) {
      transformedVideoInput.transformedVideoTrack.unpipe();
    }
    processor.unpipe();
  } catch (e) {
    const error = e as Error;
    LOG.error('Failed to cleanup processor', error.message);
  }

  LOG.info('Disabled and unpiped virtual background processor');
};

/**
 * Releases the extension resources that are no longer needed.
 */
export const releaseProcessor = (videoTrack: ILocalVideoTrack): void => {
  if (processor && videoTrack) {
    videoTrack.unpipe();
    processor.unpipe();
    processor.release();
    processor = null;
    LOG.info('Released virtual background processor');
  }
};

/**
 * ============================================
 * PREVIEW PROCESSOR FUNCTIONS
 * These functions manage a separate processor for preview
 * without affecting the main video feed
 * ============================================
 */

/**
 * Initializes the preview virtual background extension
 * This is separate from the main processor to allow previewing backgrounds
 * @returns {void}
 */
export const initializePreviewProcessor = async (): Promise<void> => {
  if (previewProcessor) return;
  try {
    // Extension is already registered globally, just create a new processor
    previewProcessor = extension.createProcessor();
    await previewProcessor.init(wasmPath);
    LOG.info('Preview virtual background processor initialized successfully');
  } catch (e) {
    previewProcessor = null;
    const error = e as Error;
    throw new Error(`Error initializing preview virtual background: ${error.message}`);
  }
};

/**
 * Disables and releases preview virtual background processor completely
 * This doesn't affect the main video processor
 * @param shouldPromote - Whether to promote preview processor to main (true on submit, false on cancel)
 * @returns {Promise<void>}
 */
export const clearPreviewVirtualBackground = async (shouldPromote = true): Promise<void> => {
  try {
    if (shouldPromote) {
      // Clean up old main processor before promoting
      if (processor) {
        await processor.disable();
        processor.unpipe();
        processor.release();
        LOG.info('Old main processor cleaned up');
      }

      // Promote preview to main
      processor = previewProcessor;
      previewProcessor = null;

      previewVideoTrack = null;

      LOG.info('Preview promoted to main successfully');
    } else {
      // Cancel: Clean up preview processor and track without affecting main
      if (previewProcessor) {
        await previewProcessor.disable();
        previewProcessor.unpipe();
        previewProcessor.release();
        previewProcessor = null;
        LOG.info('Preview processor cleaned up');
      }

      if (previewVideoTrack) {
        previewVideoTrack.unpipe();
        previewVideoTrack.close();
        previewVideoTrack = null;
        LOG.info('Preview video track cleaned up');
      }

      LOG.info('Cancel cleanup complete - main processor should be unaffected');
    }
  } catch (e) {
    const error = e as Error;
    LOG.error(`Error ${shouldPromote ? 'promoting preview to main' : 'cleaning up preview on cancel'}`, error.message);
    throw error;
  }
};

/**
 * Returns the configuration for the virtual background effect
 * @param filterType
 * @param backgroundImageURL
 * @returns {{type: string, blurDegree?: number, source?: HTMLImageElement}}
 */
export const getVFXConfig = async (
  filterType: BACKGROUND_OPTIONS,
  backgroundImageURL: string = ''
): Promise<VFXConfig | undefined> => {
  if (filterType === BACKGROUND_OPTIONS.BLUR) {
    return {
      type: 'blur',
      blurDegree: 3
    };
  }

  if (filterType === BACKGROUND_OPTIONS.CUSTOM) {
    if (!backgroundImageURL) {
      return {
        type: 'color',
        color: '#5C617C'
      };
    }

    // Wait for the image to load before returning
    const img = new Image();
    // Enable CORS for canvas usage in virtual background processor
    img.crossOrigin = 'anonymous';

    return new Promise(resolve => {
      img.onload = () => {
        LOG.info('Custom background image loaded successfully', {
          width: img.width,
          height: img.height,
          src: backgroundImageURL
        });
        resolve({
          type: 'img',
          source: img
        });
      };

      img.onerror = (event: Event | string) => {
        LOG.error('Failed to load custom background image', { error: event, src: backgroundImageURL });
        // Fallback to color background if image fails to load
        resolve({
          type: 'color',
          color: '#5C617C'
        });
      };

      img.src = backgroundImageURL;
    });
  }
  return undefined;
};

/**
 * Get video input based on option
 * @param option
 * @param transformedVideoInput
 * @param selectedVideoInputId
 * @param onTransformedVideoInputChange
 * @param backgroundUrl
 * @param isPreview - If true, uses preview processor instead of main processor (doesn't affect main video)
 * @returns {Promise<ILocalVideoTrack>}
 */
export const getVideoInput = async (
  option: BACKGROUND_OPTIONS,
  transformedVideoInput: TransformedVideoInput | null,
  selectedVideoInputId: string,
  onTransformedVideoInputChange: (input: { transformedVideoTrack?: ICameraVideoTrack; device?: string }) => void,
  backgroundUrl = '',
  isPreview = false
): Promise<{ transformedVideoTrack?: ICameraVideoTrack; device?: string } | TransformedVideoInput> => {
  const videoFxConfig = await getVFXConfig(option, backgroundUrl);
  const userHasBackground = option !== BACKGROUND_OPTIONS.NONE;

  let videoInput: { transformedVideoTrack?: ICameraVideoTrack; device?: string } = {
    device: selectedVideoInputId
  };

  // Clean up processor when switching to NONE
  if (!userHasBackground && transformedVideoInput) {
    const currentProcessor = isPreview ? previewProcessor : processor;
    if (currentProcessor) {
      await currentProcessor.disable();
      transformedVideoInput.transformedVideoTrack?.unpipe();
      currentProcessor.unpipe();
    }
    return videoInput;
  }

  const isFirstTimeBackgroundChange = userHasBackground && !transformedVideoInput;
  const isSameDevice = transformedVideoInput?.device === selectedVideoInputId;
  const isUpdatingBackground = userHasBackground && transformedVideoInput && isSameDevice;
  const isChangingDevice = userHasBackground && transformedVideoInput && !isSameDevice;

  // Create track when needed
  const needsNewTrack = (isChangingDevice || isFirstTimeBackgroundChange) && videoFxConfig;
  const videoTrack = needsNewTrack
    ? await AgoraRTC.createCameraVideoTrack({
        cameraId: selectedVideoInputId,
        encoderConfig: AGORA_ENCODER_CONFIG
      })
    : null;

  if (isFirstTimeBackgroundChange && videoFxConfig && videoTrack) {
    if (isPreview) {
      previewVideoTrack = videoTrack;
    }
    const result = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId, isPreview);
    onTransformedVideoInputChange(result);
    return result;
  }

  if (isChangingDevice && videoFxConfig && videoTrack) {
    if (isPreview) {
      // Clean up old preview track and processor before switching devices
      if (previewVideoTrack) {
        previewVideoTrack.unpipe();
      }
      if (previewProcessor) {
        previewProcessor.unpipe();
      }
      previewVideoTrack = videoTrack;
    } else {
      transformedVideoInput?.transformedVideoTrack?.unpipe();
      processor?.unpipe();
    }

    const result = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId, isPreview);
    onTransformedVideoInputChange(result);
    return result;
  }

  if (isUpdatingBackground && videoFxConfig) {
    if (isPreview) {
      // Preview needs its own track - create if it doesn't exist
      if (!previewVideoTrack) {
        previewVideoTrack = await AgoraRTC.createCameraVideoTrack({
          cameraId: selectedVideoInputId,
          encoderConfig: AGORA_ENCODER_CONFIG
        });

        // First time creating preview track, need to initialize processor
        const result = await transformVideo(previewVideoTrack, videoFxConfig, selectedVideoInputId, isPreview);
        onTransformedVideoInputChange(result);
        return result;
      }

      // Preview track exists, just update the processor with the new effect
      await updateBackgroundFilter(videoFxConfig, previewVideoTrack, isPreview);
      const result = {
        transformedVideoTrack: previewVideoTrack,
        device: selectedVideoInputId
      };
      onTransformedVideoInputChange(result);
      return result;
    } else {
      const existingTrack = transformedVideoInput.transformedVideoTrack;
      await updateBackgroundFilter(videoFxConfig, existingTrack, isPreview);
      onTransformedVideoInputChange(transformedVideoInput);
      return transformedVideoInput;
    }
  }

  return videoInput;
};
